/**
 * Which activity lens a transaction page opens on.
 *
 * Two decisions, kept out of the component so they can be read and tested
 * without mounting a graph: what `?tab=` is allowed to say, and whether the
 * page's own settling — which runs once the body arrives, a beat after the
 * first paint — is allowed to overrule it.
 *
 * The settling is why this needs care. The lens the page picks depends on data
 * that is not there yet, so the effect fires late; before `?tab=` existed that
 * was invisible, and afterwards it meant a link somebody shared as
 * `?tab=events` opened Events and then flipped to Semantic a second later.
 */
export type TransactionTab =
  | "semantic"
  | "flow"
  | "io"
  | "call"
  | "events"
  | "internal"
  | "raw";

/** The spellings `?tab=` accepts. */
export const TRANSACTION_TABS: readonly TransactionTab[] = [
  "semantic",
  "flow",
  "io",
  "call",
  "events",
  "internal",
  "raw",
];

/**
 * The lens a link asked for, or `null` when it asked for nothing usable.
 *
 * `null` rather than a default: the caller's fallback depends on the body, and
 * "the link named no lens" is what tells the settling below that it may act.
 */
export function transactionTabFromHint(hint: string | undefined | null): TransactionTab | null {
  const value = (hint ?? "").trim().toLowerCase();
  return (TRANSACTION_TABS as readonly string[]).includes(value)
    ? (value as TransactionTab)
    : null;
}

/**
 * The lens a link asked for, dropped when that lens is not on the bar.
 *
 * Raw is diagnostic and only rendered under `?debug=1`, so a link saved while
 * debug was on names a tab that is not there for the next reader: the panel
 * selects it, nothing matches, and the card renders empty with no tab
 * highlighted and no way back. Dropping it lets the usual fallback choose.
 *
 * Separate from {@link transactionTabFromHint} because the two answer different
 * questions — "is this a lens at all" does not depend on who is looking, and
 * this does.
 */
export function offeredTransactionTab(
  deepLinked: TransactionTab | null,
  { showRaw }: { showRaw: boolean },
): TransactionTab | null {
  return deepLinked === "raw" && !showRaw ? null : deepLinked;
}

export interface SettleInputs {
  /** The lens `?tab=` named, from {@link transactionTabFromHint}. */
  deepLinked: TransactionTab | null;
  /** The body carries a semantic story. */
  hasSemantic: boolean;
  /** The body carries a decoded call. */
  hasDecodedCall: boolean;
}

/**
 * The lens to settle on once the body loads, or `null` to leave the tab alone.
 *
 * Semantic when there is a story, else the decoded call, else the flow graph —
 * and never anything at all when the reader followed a link to a specific lens.
 */
export function settledTransactionTab({
  deepLinked,
  hasSemantic,
  hasDecodedCall,
}: SettleInputs): TransactionTab | null {
  if (deepLinked) return null;
  if (hasSemantic) return "semantic";
  return hasDecodedCall ? "call" : "flow";
}

export interface EmptyDecodedTabInputs {
  /** What the tab lists, plural: "events", "internal transactions". */
  what: string;
  /** The decoded body has arrived. */
  hasBody: boolean;
  /** The detail read produced nothing — denied, or failed. */
  unresolved: boolean;
  /** The read or the background decode is still running. */
  decodePending: boolean;
}

/**
 * What an EMPTY Events / Internal tab says — by why it is empty.
 *
 * "Nothing decoded for this view" is a claim about a FINISHED decode, and it
 * used to be the only line these tabs had. A transaction still being decoded is
 * a `processing` stub — chain facts, no body yet — so a link to
 * `?tab=internal` on a transaction opened for the first time said its decode
 * had found no internal calls while the decode was still running.
 *
 * The same ladder the value-flow tab already had: a body that holds none, a
 * read that never resolved, a decode in progress, and storage that has nothing.
 */
export function emptyDecodedTabText({
  what,
  hasBody,
  unresolved,
  decodePending,
}: EmptyDecodedTabInputs): string {
  if (hasBody) return "Nothing decoded for this view.";
  if (unresolved) return `This transaction's details could not be loaded, so its ${what} are unknown.`;
  if (decodePending) return `Still decoding this transaction — its ${what} appear when it finishes.`;
  return `No payload is stored for this transaction, so there are no ${what} to show.`;
}

/**
 * One part of the transaction a link can point at (`?focus=`): the top-level
 * call, one event log, or one internal transaction — named exactly as the
 * quality assessment names them (`call`, `event:<logIndex>`, `internal:<n>`),
 * so a part the debug card lists links to the row a reader can inspect.
 */
export interface TransactionFocus {
  /** The lens the part is shown on. */
  tab: "call" | "events" | "internal";
  /** The log index / internal position; `null` for the call. */
  index: number | null;
  /** The canonical spelling, as `?focus=` carries it. */
  id: string;
}

/** The part a `?focus=` value names, or `null` when it names none. */
export function transactionFocusFromHint(hint: string | undefined | null): TransactionFocus | null {
  const value = (hint ?? "").trim().toLowerCase();
  if (value === "call") return { tab: "call", index: null, id: "call" };
  const match = /^(event|internal):(\d+)$/.exec(value);
  if (!match) return null;
  return { tab: match[1] === "event" ? "events" : "internal", index: Number(match[2]), id: value };
}

/**
 * The part a quality artifact belongs to. A payload's id extends its carrier's
 * (`internal:6.arg6_bytes` is a bytes argument of `internal:6`), so it links to
 * the call that carried it. `null` for parts no lens lists (a cross-chain
 * message, a Solana instruction).
 */
export function focusForArtifact(artifactId: string): TransactionFocus | null {
  return transactionFocusFromHint(artifactId.split(".")[0]);
}

/**
 * The position in `items` of the row `focus` names, or `null`.
 *
 * Rows carry the index the decode gave them (`ev.index`, `itx.index`); a list
 * whose rows carry none is matched by position, the order the assessment
 * counted them in.
 */
export function focusedRow<T extends { index?: number | null }>(
  items: readonly T[],
  focus: TransactionFocus | null,
  tab: TransactionFocus["tab"],
): number | null {
  if (!focus || focus.tab !== tab || focus.index === null) return null;
  const byIndex = items.findIndex((item) => item.index === focus.index);
  if (byIndex >= 0) return byIndex;
  return items.every((item) => item.index == null) && focus.index < items.length ? focus.index : null;
}
